import React from 'react';
import { StatCard } from '../../shared/StatCard';

const AnalyticsTab = ({ analyticsData, loading, loadAnalyticsData, analyticsRange, setAnalyticsRange }) => {
  const ranges = [
    { value: '7d', label: 'Last 7 days' },
    { value: '28d', label: 'Last 28 days' },
    { value: '3m', label: 'Last 3 months' },
    { value: '6m', label: 'Last 6 months' },
    { value: '12m', label: 'Last 12 months' },
    { value: '14m', label: 'Last 14 months' }
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800">Google Analytics Statistics</h2>
        <div className="flex items-center gap-2">
          <select
            className="border rounded-md px-3 py-2 text-sm bg-white"
            value={analyticsRange}
            onChange={(e) => setAnalyticsRange(e.target.value)}
            disabled={loading}
          >
            {ranges.map((range) => (
              <option key={range.value} value={range.value}>
                {range.label}
              </option>
            ))}
          </select>
          <button 
            onClick={() => loadAnalyticsData(analyticsRange)}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-2 px-4 rounded flex items-center"
          >
            <i className={`fa-solid fa-sync-alt mr-2 ${loading ? 'animate-spin' : ''}`}></i>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>
      
      {/* Basic Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          title="Page Views"
          value={loading ? 'Loading...' : analyticsData.basicStats.pageViews.toLocaleString()}
          icon="fa-eye"
          color="blue"
        />
        <StatCard
          title="Unique Users"
          value={loading ? 'Loading...' : analyticsData.basicStats.uniqueUsers.toLocaleString()}
          icon="fa-users"
          color="green"
        />
        <StatCard
          title="Avg. Session"
          value={loading ? 'Loading...' : analyticsData.basicStats.avgSessionDuration}
          icon="fa-clock"
          color="yellow"
        />
        <StatCard
          title="Bounce Rate"
          value={loading ? 'Loading...' : analyticsData.basicStats.bounceRate}
          icon="fa-chart-line"
          color="purple"
        />
      </div>

      {/* Real-time Data */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <StatCard
          title="Active Users (Real-time)"
          value={loading ? 'Loading...' : analyticsData.realTimeData.activeUsers}
          icon="fa-signal"
          color="red"
        />
        <StatCard
          title="Current Page Views"
          value={loading ? 'Loading...' : analyticsData.realTimeData.currentPageViews}
          icon="fa-chart-bar"
          color="indigo"
        />
      </div>

      {/* Top Pages Table */}
      <div className="bg-white border rounded-lg p-4 mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Pages</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Page</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unique Views</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analyticsData.topPages.map((page, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{page.page}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{page.views.toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{page.uniqueViews.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Demographics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* User Countries */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Users by Country</h3>
          <div className="space-y-3">
            {analyticsData.userCountries.map((country, index) => (
              <div key={index} className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{country.country}</span>
                <span className="text-sm font-semibold text-gray-900">{country.users.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>

        {/* User Organizations */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Users by Organization</h3>
          <div className="space-y-3">
            {analyticsData.userOrganizations.map((org, index) => (
              <div key={index} className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{org.organization}</span>
                <span className="text-sm font-semibold text-gray-900">{org.users.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Device Types */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Users by Device</h3>
          <div className="space-y-3">
            {analyticsData.deviceTypes.map((device, index) => (
              <div key={index} className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{device.device}</span>
                <span className="text-sm font-semibold text-gray-900">{device.users.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsTab; 